import React, { createContext, useContext } from 'react';
import { Snackbar, Alert } from '@mui/material';
import useAuthStore from '../store/authStore';
import { isCbcFaculty as checkCbcFaculty } from '../utils/facultyRole';


const AuthContext = createContext();
/**
 * useAuth – drop-in replacement hook.
 * Pulls everything from the Zustand store so existing components
 * don't need to change their import path yet.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

/**
 * AuthProvider – initialises the store on mount and renders the
 * global snackbar. Kept as a wrapper so App.js doesn't change yet.
 */
export const AuthProvider = ({ children }) => {
  const { user,  snackbar, login, logout, closeSnackbar } = useAuthStore();

  const value = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    isStudent: user?.role === 'student',
    isFaculty: user?.role === 'faculty',
    isCbcFaculty: checkCbcFaculty(user),
    isAdmin: user?.role === 'admin',
    loading: false, // hydration is synchronous via zustand/persist
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AuthContext.Provider>
  );
};

export default useAuthStore;
